/**
 * Offers a reload when a newer version of the app has been deployed.
 *
 * Deliberately an offer, not an action. Someone may be part-way through a
 * questionnaire, a chat turn or a care note, and losing that to an automatic
 * reload would be worse than running a slightly old build. It is also
 * dismissible: saying "not now" has to mean something, or the next prompt is
 * just noise.
 *
 * Not rendered during a video call at all — see App.js. A reload would drop the
 * call.
 *
 * Note on the close button: MUI's Alert renders its own close control from
 * `onClose` ONLY when no `action` prop is supplied. Passing `action` replaces
 * it, so both controls are declared here explicitly. Relying on `onClose`
 * alongside `action` silently leaves the banner with no way to dismiss it.
 */
import React, { useState } from 'react';
import { Alert, Button, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export default function AppUpdateBanner({ available, onReload }) {
  const [dismissed, setDismissed] = useState(false);

  if (!available || dismissed) return null;

  const reload = () => {
    if (onReload) onReload();
    else window.location.reload();
  };

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ zIndex: (t) => t.zIndex.snackbar }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <>
            <Button color="inherit" size="small" onClick={reload} sx={{ fontWeight: 600 }}>
              Reload
            </Button>
            <IconButton
              size="small"
              color="inherit"
              aria-label="Close"
              onClick={() => setDismissed(true)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        sx={{ alignItems: 'center' }}
      >
        A new version of AIMHI is available.
      </Alert>
    </Snackbar>
  );
}
